import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowDown, ArrowLeft, ChevronRight, X } from 'lucide-react';
import { caseStudies } from '@/data/caseStudiesData';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
};

/* ── Animated Number ── */
function AnimNum({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const [display, setDisplay] = useState('0');
  const numStr = value.replace(/[^0-9.]/g, '');
  const num = parseFloat(numStr);

  useEffect(() => {
    if (!inView) return;
    let s = 0;
    const dur = 2000, step = Math.max(num / (dur / 16), 0.1);
    const t = setInterval(() => {
      s += step;
      if (s >= num) {
        setDisplay(value);
        clearInterval(t);
      } else {
        const prefix = value.match(/^\D*/)?.[0] || '';
        const suffix = value.replace(numStr, '').replace(prefix, '');
        if (num >= 1000000000) setDisplay(prefix + (s / 1e9).toFixed(1) + suffix);
        else if (num >= 1000000) setDisplay(prefix + (s / 1e6).toFixed(1) + suffix);
        else if (num >= 1000) setDisplay(prefix + Math.floor(s).toLocaleString() + suffix);
        else setDisplay(prefix + Math.floor(s) + suffix);
      }
    }, 16);
    return () => clearInterval(t);
  }, [inView, num, value, numStr]);

  return <span ref={ref}>{display}</span>;
}

/* ── Sticky Nav ── */
function StickyNav({ sections, activeId }) {
  return (
    <div className="fixed right-8 top-1/2 -translate-y-1/2 z-40 hidden xl:flex flex-col gap-3">
      {sections.map((s) => (
        <a key={s.id} href={`#${s.id}`}
          className={`text-[10px] tracking-widest uppercase text-right transition-all duration-300 ${activeId === s.id ? 'text-foreground font-semibold' : 'text-muted-foreground/50 hover:text-muted-foreground'}`}
        >{s.label}</a>
      ))}
    </div>
  );
}

/* ── Service Chip ── */
function ServiceChip({ label, delay }) {
  return (
    <motion.span initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay }}
      className="px-5 py-2.5 text-xs tracking-widest uppercase border border-border rounded-full hover:bg-butter hover:border-butter transition-colors duration-300 cursor-default"
    >{label}</motion.span>
  );
}

/* ── Timeline Step ── */
function TimelineStep({ step, index, total }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: index * 0.1 }}
      className="flex flex-col items-center text-center relative"
    >
      <div className="w-12 h-12 rounded-full bg-butter flex items-center justify-center mb-3 relative z-10">
        <span className="font-serif text-sm font-medium">{String(index + 1).padStart(2, '0')}</span>
      </div>
      <p className="text-sm font-medium tracking-wide">{step}</p>
      {index < total - 1 && <ChevronRight className="h-4 w-4 text-muted-foreground rotate-90 mt-3 md:hidden" />}
    </motion.div>
  );
}

/* ── Metric Big ── */
function MetricBig({ value, label, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-30px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay }} className="text-center p-6">
      <div className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-2"><AnimNum value={value} /></div>
      <p className="text-sm tracking-widest uppercase text-muted-foreground">{label}</p>
    </motion.div>
  );
}

/* ── Infographic Card ── */
function InfoCard({ title, items, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-30px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay }}
      className="p-8 bg-secondary border border-border"
    >
      <h4 className="font-serif text-lg font-medium mb-6">{title}</h4>
      <div className="space-y-4">
        {items.map((item, i) => (
          <div key={i} className="flex items-center justify-between border-b border-border/50 pb-3 last:border-0">
            <span className="text-sm text-muted-foreground">{item.label}</span>
            <span className="font-serif text-lg font-medium">{item.value}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

const NAV = [
  { id: 'aw-overview', label: 'Overview' },
  { id: 'aw-challenge', label: 'Challenge' },
  { id: 'aw-strategy', label: 'Strategy' },
  { id: 'aw-execution', label: 'Execution' },
  { id: 'aw-results', label: 'Results' },
  { id: 'aw-earned', label: 'Earned Media' },
  { id: 'aw-next', label: 'Next' },
];

const SERVICES = ['Marketing Strategy', 'Creative Direction', 'Media Buying', 'Website Design', 'Campaign Branding', 'Photography', 'Video Production', 'Social Media', 'Influencer Marketing', 'Experiential Marketing', 'Digital Advertising', 'Email Marketing', 'Search Marketing', 'Out-of-Home Advertising', 'Public Relations'];

const TIMELINE = ['Research', 'Audience Targeting', 'Creative Direction', 'Integrated Media Planning', 'Content Production', 'Influencer Partnerships', 'Experiential Activations', 'Performance Optimization'];

const CHANNELS = [
  { title: 'Social Media', items: ['Facebook', 'Instagram', 'X', 'YouTube', 'Connected TV'] },
  { title: 'Digital Advertising', items: ['Google Search', 'Google Display', 'Taboola Native Ads', 'Premium Publisher Placements', 'Miami Herald', 'Miami New Times'] },
  { title: 'Out-of-Home', items: ['Bus Shelters', 'Street Pole Banners', 'South Beach Trolley Wrap', 'Lincoln Road Sand Sculpture', 'Interactive QR Codes'] },
  { title: 'Owned Media', items: ['Dedicated Website', 'Email Marketing', 'Website Content', 'Social Channels', 'Event Calendar'] },
];

const CAMPAIGN_METRICS = [
  { value: '7.7M+', label: 'Campaign Impressions' },
  { value: '50,837', label: 'Website Views' },
  { value: '19,689', label: 'Digital & Social Clicks' },
  { value: '353,983', label: 'Video Views' },
  { value: '1.9B', label: 'Earned Media Reach' },
  { value: '108', label: 'Media Outlets' },
];

/* ── Campaign Gallery Slideshow ── */
function CampaignGallery() {
  const study = caseStudies.find(s => s.slug === 'art-week-miami-beach');
  const images = study ? [study.heroImage, ...(study.galleryImages || [])] : [];
  const [idx, setIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowRight') setIdx(p => (p + 1) % images.length);
      if (e.key === 'ArrowLeft') setIdx(p => (p - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen, images.length]);

  if (images.length === 0) return null;

  return (
    <>
      <section className="py-28 md:py-36" data-testid="aw-gallery">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-12">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Campaign Collateral</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium">Selected campaign assets.</motion.h2>
          </motion.div>

          {/* Main slideshow */}
          <div className="max-w-5xl mx-auto mb-6">
            <div className="relative bg-white border border-border">
              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="cursor-pointer"
                  onClick={() => setLightboxOpen(true)}
                >
                  <img
                    src={images[idx]}
                    alt={`Art Week campaign asset ${idx + 1}`}
                    className="w-full h-[55vh] md:h-[65vh] object-contain mx-auto"
                  />
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex items-center justify-between mt-4">
              <button onClick={() => setIdx(p => (p - 1 + images.length) % images.length)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-5 w-5" /> Previous
              </button>
              <span className="text-sm text-muted-foreground font-medium tracking-widest">{idx + 1} / {images.length}</span>
              <button onClick={() => setIdx(p => (p + 1) % images.length)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                Next <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Thumbnail strip */}
          <div className="flex gap-2 justify-center flex-wrap max-w-5xl mx-auto">
            {images.slice(0, 30).map((img, i) => (
              <button key={i} onClick={() => setIdx(i)} className={`w-14 h-14 overflow-hidden border transition-colors duration-200 ${idx === i ? 'border-butter' : 'border-border hover:border-butter/50'}`}>
                <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
            {images.length > 30 && <span className="w-14 h-14 flex items-center justify-center text-xs text-muted-foreground border border-border">+{images.length - 30}</span>}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
            <button onClick={() => setLightboxOpen(false)} className="absolute top-6 right-6 text-white/60 hover:text-white z-50"><X className="h-8 w-8" /></button>
            <button onClick={(e) => { e.stopPropagation(); setIdx(p => (p - 1 + images.length) % images.length); }} className="absolute left-6 text-white/60 hover:text-white z-50"><ArrowLeft className="h-8 w-8" /></button>
            <button onClick={(e) => { e.stopPropagation(); setIdx(p => (p + 1) % images.length); }} className="absolute right-6 text-white/60 hover:text-white z-50"><ArrowRight className="h-8 w-8" /></button>
            <img src={images[idx]} alt="" className="max-w-[90vw] max-h-[85vh] object-contain" onClick={(e) => e.stopPropagation()} />
            <span className="absolute bottom-6 text-white/40 text-sm tracking-widest">{idx + 1} / {images.length}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default function ArtWeekPage() {
  const [activeSection, setActiveSection] = useState('aw-overview');
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); }); },
      { rootMargin: '-40% 0px -40% 0px' }
    );
    NAV.forEach((s) => { const el = document.getElementById(s.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-32" data-testid="art-week-page">
      <StickyNav sections={NAV} activeId={activeSection} />

      {/* ═══ HERO ═══ */}
      <section ref={heroRef} className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-foreground" data-testid="aw-hero">
        <motion.div style={{ y: heroY }} className="absolute inset-0 z-0">
          <img src="/images/aw-hero.png" alt="Art Week Miami Beach" className="w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/50 to-foreground/30" />
        </motion.div>
        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeInUp} className="text-xs tracking-[0.4em] uppercase text-butter mb-4">City of Miami Beach</motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl lg:text-7xl font-medium text-background leading-[1.08] mb-4">
              Art Week Miami Beach
            </motion.h1>
            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-3 mb-8 flex-wrap">
              {['Integrated Marketing', 'Creative Direction', 'Digital Strategy'].map((s, i) => (
                <span key={i} className="text-xs tracking-widest uppercase text-background/50">{i > 0 && <span className="mr-3">•</span>}{s}</span>
              ))}
            </motion.div>
            <motion.p variants={fadeInUp} className="text-background/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-12">
              Bringing one of the world's most recognized art weeks to life through an integrated, citywide marketing campaign.
            </motion.p>
            <motion.a variants={fadeInUp} href="#aw-overview"
              className="inline-flex items-center gap-2 text-butter text-sm tracking-widest uppercase hover:text-butter-dark transition-colors"
            >
              Explore the Campaign
              <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 2, repeat: Infinity }}><ArrowDown className="h-4 w-4" /></motion.span>
            </motion.a>
          </motion.div>
        </motion.div>
      </section>

      {/* ═══ OVERVIEW ═══ */}
      <section id="aw-overview" className="py-28 md:py-36">
        <div className="container-main max-w-5xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-6">Overview</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight mb-10">
              A fully integrated campaign for one of the world's premier art destinations.
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-3xl">
              Art Week Miami Beach required a fully integrated marketing campaign to promote Art Week 2023, including the internationally recognized No Vacancy public art program, temporary public art installations, the Art Basel Legacy Purchase initiative and citywide cultural programming. The objective was to increase awareness, drive attendance, encourage community engagement and position Miami Beach as a premier global destination for arts and culture.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mt-16">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-6">Services</motion.p>
            <div className="flex flex-wrap gap-3">
              {SERVICES.map((s, i) => <ServiceChip key={s} label={s} delay={i * 0.04} />)}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══ CHALLENGE ═══ */}
      <section id="aw-challenge" className="py-28 md:py-36">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24 max-w-6xl mx-auto items-start">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">The Challenge</motion.p>
              <motion.h2 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.08]">
                One City. Hundreds of Events. One Unified Story.
              </motion.h2>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-6">
              <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed">
                The campaign needed to communicate multiple city initiatives simultaneously while creating excitement around Art Week, supporting public art programs and driving participation across residents and visitors. Campaign priorities included:
              </motion.p>
              <motion.ul variants={fadeInUp} className="space-y-4">
                {[
                  'Promoting Art Week Miami Beach across all channels',
                  'Elevating the No Vacancy public art program',
                  'Supporting temporary public art installations citywide',
                  'Promoting the Art Basel Legacy Purchase initiative',
                  'Increasing awareness across residents and visitors',
                  'Driving traffic across digital and physical touchpoints',
                ].map((item, i) => (
                  <motion.li key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.08 }} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-butter mt-2.5 flex-shrink-0" />
                    <span className="text-foreground text-sm md:text-base leading-relaxed">{item}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══ STRATEGY TIMELINE ═══ */}
      <section id="aw-strategy" className="py-28 md:py-36">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-20">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Campaign Strategy</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium">An integrated roadmap.</motion.h2>
          </motion.div>
          <div className="relative max-w-6xl mx-auto">
            <div className="hidden md:block absolute top-6 left-0 right-0 h-px bg-border" />
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6 md:gap-3">
              {TIMELINE.map((step, i) => <TimelineStep key={step} step={step} index={i} total={TIMELINE.length} />)}
            </div>
          </div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══ MULTI-CHANNEL EXECUTION ═══ */}
      <section id="aw-execution" className="py-28 md:py-36 bg-secondary">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-16">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Multi-Channel Execution</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium mb-6">Every touchpoint. One campaign.</motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-16">
            {CHANNELS.map((ch, i) => (
              <motion.div key={ch.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.6 }}
                className="p-8 bg-background border border-border hover:border-butter/50 transition-all duration-500 group"
              >
                <h3 className="font-serif text-lg font-medium mb-5 group-hover:text-butter-dark transition-colors duration-300">{ch.title}</h3>
                <ul className="space-y-2.5">
                  {ch.items.map((item) => (
                    <li key={item} className="text-sm text-muted-foreground flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-butter flex-shrink-0" />{item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Influencer Section */}
          <div className="max-w-5xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
              className="p-10 md:p-14 bg-background border border-border"
            >
              <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Influencer Marketing</motion.p>
              <motion.h3 variants={fadeInUp} className="font-serif text-2xl md:text-3xl font-medium mb-4">Local voices amplifying art.</motion.h3>
              <motion.p variants={fadeInUp} className="text-muted-foreground text-base leading-relaxed mb-6 max-w-2xl">
                Three local influencers were selected to amplify the No Vacancy program through VIP guided tours and social storytelling, generating a combined audience reach of approximately 59,400 followers.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-butter flex items-center justify-center font-serif text-lg font-medium">3</div>
                <div>
                  <p className="font-medium text-sm">Local Influencers</p>
                  <p className="text-muted-foreground text-sm">59,400 combined reach</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ CAMPAIGN GALLERY SLIDESHOW ═══ */}
      <CampaignGallery />

      {/* ═══ RESULTS ═══ */}
      <section id="aw-results" className="py-28 md:py-36">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-20">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Campaign Performance</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium">Measurable impact at scale.</motion.h2>
          </motion.div>

          {/* Top-level metrics */}
          <div className="max-w-5xl mx-auto mb-20">
            <div className="border border-border p-10 md:p-14">
              <h3 className="font-serif text-2xl font-medium mb-2 text-center">Campaign Performance</h3>
              <p className="text-muted-foreground text-sm text-center mb-12 tracking-wide">Art Week Miami Beach 2023</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                {CAMPAIGN_METRICS.map((m, i) => <MetricBig key={m.label} value={m.value} label={m.label} delay={i * 0.1} />)}
              </div>
            </div>
          </div>

          {/* Paid + Social + Email breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-20">
            <InfoCard title="Paid Media" delay={0} items={[
              { label: 'Impressions', value: '7.08M' },
              { label: 'Link Clicks', value: '19,376' },
              { label: 'OOH Impressions', value: '3.27M' },
              { label: 'Social + Influencer', value: '2.1M' },
              { label: 'Digital + Native', value: '1M' },
            ]} />
            <InfoCard title="Social Performance" delay={0.1} items={[
              { label: 'Organic Posts', value: '248' },
              { label: 'Impressions', value: '341K' },
              { label: 'Engagements', value: '17,699' },
              { label: 'Video Views', value: '64,174' },
              { label: 'Global Social Posts', value: '218K' },
            ]} />
            <InfoCard title="Email Marketing" delay={0.2} items={[
              { label: 'Impressions', value: '280,199' },
              { label: 'Campaigns Sent', value: '11' },
              { label: 'Open Rate', value: '39%' },
            ]} />
          </div>

          {/* Distribution breakdown */}
          <div className="max-w-4xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-10">
              <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Campaign Distribution</motion.p>
            </motion.div>
            <div className="grid grid-cols-3 gap-6">
              {[
                { label: 'No Vacancy', value: '69.9%', color: 'bg-foreground' },
                { label: 'Art Week', value: '25.1%', color: 'bg-butter' },
                { label: 'Elevate Española', value: '4.3%', color: 'bg-muted-foreground' },
              ].map((item, i) => (
                <motion.div key={item.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center">
                  <div className="font-serif text-3xl sm:text-4xl font-medium mb-2">{item.value}</div>
                  <div className={`h-1.5 ${item.color} rounded-full mb-3`} style={{ width: item.value }} />
                  <p className="text-sm tracking-widest uppercase text-muted-foreground">{item.label}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Global Conversation */}
          <div className="max-w-4xl mx-auto mt-20 p-10 md:p-14 border border-border text-center">
            <p className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Global Conversation</p>
            <div className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight mb-4">3B+</div>
            <p className="text-sm tracking-widest uppercase text-muted-foreground mb-8">Estimated Global Impressions</p>
            <div className="grid grid-cols-3 gap-8 max-w-xl mx-auto">
              <div className="text-center">
                <div className="font-serif text-2xl font-medium">218K</div>
                <p className="text-xs text-muted-foreground mt-1">Social Posts</p>
              </div>
              <div className="text-center">
                <div className="font-serif text-2xl font-medium">11.5M</div>
                <p className="text-xs text-muted-foreground mt-1">Engagements</p>
              </div>
              <div className="text-center">
                <div className="font-serif text-2xl font-medium">99,944</div>
                <p className="text-xs text-muted-foreground mt-1">Unique Authors</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ EARNED MEDIA ═══ */}
      <section id="aw-earned" className="py-28 md:py-36 bg-foreground text-background">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-8">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-background/40 mb-4">Earned Media</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-background mb-8">
              Global attention beyond paid media.
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-background/50 text-base md:text-lg leading-relaxed max-w-3xl mx-auto mb-4">
              The campaign generated more than 230 media stories across 108 media outlets, resulting in an estimated 1.9–2 billion earned impressions, significantly extending the campaign's reach beyond paid and owned channels.
            </motion.p>
          </motion.div>

          <div className="max-w-3xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            {[
              { val: '230+', label: 'Media Stories' },
              { val: '108', label: 'Media Outlets' },
              { val: '1.9B', label: 'Earned Impressions' },
              { val: '615K', label: 'News Impressions' },
            ].map((m, i) => (
              <motion.div key={m.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center">
                <div className="font-serif text-3xl sm:text-4xl font-medium text-butter mb-1">{m.val}</div>
                <p className="text-[10px] tracking-widest uppercase text-background/40">{m.label}</p>
              </motion.div>
            ))}
          </div>

          {/* Scrolling media logos */}
          <div className="overflow-hidden mb-16">
            <motion.div animate={{ x: ['0%', '-50%'] }} transition={{ duration: 35, repeat: Infinity, ease: 'linear' }} className="flex gap-4">
              {[...['Miami Herald', 'Art Basel', 'Artnet', 'Art News', 'Hyperallergic', 'Ocean Drive', 'Miami New Times', 'South Florida Sun Sentinel', 'WSVN', 'CBS Miami'],
                ...['Miami Herald', 'Art Basel', 'Artnet', 'Art News', 'Hyperallergic', 'Ocean Drive', 'Miami New Times', 'South Florida Sun Sentinel', 'WSVN', 'CBS Miami']].map((name, i) => (
                <div key={`${name}-${i}`} className="flex-shrink-0 px-8 py-4 border border-border/50 backdrop-blur-sm bg-background/5 rounded-sm">
                  <span className="font-serif text-lg font-medium text-background/80 whitespace-nowrap">{name}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* How Muse Delivered */}
          <div className="max-w-4xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-background/40 mb-6">How Muse Delivered</motion.p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {['Marketing Strategy & Planning', 'Creative Direction & Branding', 'Media Buying & Optimization', 'Photography & Video Production', 'Social Media Management', 'Influencer Partnerships', 'Email Marketing Campaigns', 'Out-of-Home Advertising', 'Experiential Activations', 'Public Relations Strategy'].map((d, i) => (
                  <motion.div key={d} initial={{ opacity: 0, x: -15 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                    className="flex items-center gap-3 py-3 border-b border-background/10"
                  >
                    <span className="w-2 h-2 rounded-full bg-butter flex-shrink-0" />
                    <span className="text-background/70 text-sm md:text-base">{d}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ WHY THIS MATTERS ═══ */}
      <section className="py-28 md:py-36">
        <div className="container-main text-center max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <p className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight italic mb-10">
              "Integrated campaigns require integrated thinking."
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
              This project demonstrates Muse Social Creative's ability to lead complex, multi-channel marketing initiatives that combine strategy, creative direction, paid media, influencer partnerships, experiential marketing and digital storytelling into a single cohesive campaign. From concept to execution, every touchpoint was designed to maximize awareness, engagement and measurable impact.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══ NEXT ═══ */}
      <section id="aw-next" className="py-28 md:py-36 bg-foreground">
        <div className="container-main text-center">
          <Link to="/case-studies/city-of-miami-beach" className="group block max-w-4xl mx-auto">
            <p className="text-xs tracking-widest uppercase text-background/40 mb-4">Next Project</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl font-medium text-background group-hover:text-butter transition-colors duration-500 mb-4">
              City of Miami Beach
            </h2>
            <p className="text-background/50 text-sm mb-8">Spring Break Public Awareness Campaign</p>
            <span className="inline-flex items-center gap-2 text-background/60 group-hover:text-butter transition-colors duration-300 text-sm">
              <span className="inline-flex items-center gap-2 bg-butter text-foreground px-8 py-3 rounded-full text-xs tracking-widest uppercase font-medium hover:bg-butter-dark transition-colors duration-300">View More</span>
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
